import { civilDaysPastDue, effectiveTituloStatus, parseCivilDateString } from '@/lib/utils';

export type GestorActionStatus = 'ACEITO' | 'NEGADO' | null;

export type KanbanSortBy = 'diasAtraso' | 'valorVencido';
export type KanbanSortOrder = 'asc' | 'desc';
export type KanbanViewMode = 'kanban' | 'list';

export const KANBAN_VIEW_MODE_KEY = 'kanban.viewMode';

export function readKanbanViewMode(): KanbanViewMode {
  try {
    const v = localStorage.getItem(KANBAN_VIEW_MODE_KEY);
    if (v === 'list' || v === 'kanban') return v;
  } catch {
    /* ignore */
  }
  return 'kanban';
}

export function writeKanbanViewMode(mode: KanbanViewMode): void {
  try {
    localStorage.setItem(KANBAN_VIEW_MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function getGestorActionStatus(card: {
  gestorActionStatus?: GestorActionStatus;
  feedbacks?: Array<{ tipo?: string; tipoNegociacao?: string; createdAt?: string }>;
}): GestorActionStatus {
  if (card.gestorActionStatus === 'ACEITO' || card.gestorActionStatus === 'NEGADO') {
    return card.gestorActionStatus;
  }

  if (!card.feedbacks || !Array.isArray(card.feedbacks)) {
    return null;
  }

  const gestorFeedbacks = card.feedbacks
    .filter((feedback) => feedback.tipo === 'GESTOR_RESPONSE')
    .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());

  if (gestorFeedbacks.length === 0) {
    return null;
  }

  const ultimoFeedback = gestorFeedbacks[0];
  if (ultimoFeedback.tipoNegociacao === 'GESTOR_ACEITOU') {
    return 'ACEITO';
  }
  if (ultimoFeedback.tipoNegociacao === 'GESTOR_NEGOU') {
    return 'NEGADO';
  }

  return null;
}

export function calcularDiasAtraso(titulos: Array<{ status?: string; vencimento?: string | Date }>): number {
  const titulosVencidos = titulos.filter(
    (t) =>
      effectiveTituloStatus(t.status ?? '', t.vencimento ?? '') === 'VENCIDO' || t.status === 'PARCIAL'
  );
  if (titulosVencidos.length === 0) return 0;
  const tituloMaisAntigo = titulosVencidos.reduce((maisAntigo, atual) =>
    parseCivilDateString(atual.vencimento ?? '') < parseCivilDateString(maisAntigo.vencimento ?? '')
      ? atual
      : maisAntigo
  );
  if (!tituloMaisAntigo.vencimento) return 0;
  return civilDaysPastDue(tituloMaisAntigo.vencimento);
}

/** Status de locação mais comum nos títulos do card (APROVADA → ATIVA). */
export function getStatusLocacaoFromTitulos(
  titulos: Array<{ statusLocacao?: string | null }>
): string | null {
  if (!titulos.length) return null;
  const statusCounts = new Map<string, number>();
  for (const titulo of titulos) {
    if (!titulo.statusLocacao) continue;
    const normalized =
      titulo.statusLocacao === 'APROVADA' ? 'ATIVA' : String(titulo.statusLocacao);
    statusCounts.set(normalized, (statusCounts.get(normalized) || 0) + 1);
  }
  if (statusCounts.size === 0) return null;
  let mostCommon = '';
  let maxCount = 0;
  statusCounts.forEach((count, status) => {
    if (count > maxCount) {
      maxCount = count;
      mostCommon = status;
    }
  });
  return mostCommon || null;
}

export function getOperadoresFromTitulos(
  titulos: Array<{ operador?: { id?: string; name?: string } | null }>
): Array<{ id: string; name: string }> {
  const acc: Array<{ id: string; name: string }> = [];
  for (const t of titulos) {
    const op = t.operador;
    if (!op?.id) continue;
    if (!acc.find((x) => x.id === op.id)) {
      acc.push({ id: op.id, name: op.name || 'Operador' });
    }
  }
  return acc;
}

export function getCompanyNameFromCard(card: {
  cliente?: {
    empresa?: { nome?: string };
    company?: { name?: string };
  };
  titulos?: Array<{ company?: { name?: string } }>;
}): string | null {
  const cliente = card.cliente;
  const titulos = Array.isArray(card.titulos) ? card.titulos : [];
  return (
    cliente?.empresa?.nome ??
    cliente?.company?.name ??
    titulos.find((t) => t?.company?.name)?.company?.name ??
    null
  );
}

export function getValorVencidoFromTitulos(
  titulos: Array<{ status?: string; vencimento?: string | Date; saldo?: number; valor?: number }>
): number {
  return titulos
    .filter(
      (t) =>
        effectiveTituloStatus(t.status ?? '', t.vencimento ?? '') === 'VENCIDO' ||
        t.status === 'PARCIAL'
    )
    .reduce((sum, t) => sum + Number(t.saldo || t.valor || 0), 0);
}

export function ordenarKanbanCards(
  cards: any[],
  sortBy: KanbanSortBy,
  sortOrder: KanbanSortOrder
): any[] {
  return [...cards].sort((a, b) => {
    const titulosA = Array.isArray(a.titulos) ? a.titulos : [];
    const titulosB = Array.isArray(b.titulos) ? b.titulos : [];

    let valorA: number;
    let valorB: number;

    if (sortBy === 'diasAtraso') {
      valorA = calcularDiasAtraso(titulosA);
      valorB = calcularDiasAtraso(titulosB);
    } else {
      valorA = getValorVencidoFromTitulos(titulosA);
      valorB = getValorVencidoFromTitulos(titulosB);
    }

    return sortOrder === 'asc' ? valorA - valorB : valorB - valorA;
  });
}
